// 2026-06-30 - FormuLearning - 관리자 외부 API 로그 조회 타입 (이슈 #30)
// 2026-09-26 수정 - 이슈 #95 단계 3-5: `UsageTimelineRow` 삭제(`ADM-STAT-03` 폐기).
// 2026-11-03 수정 - 이슈 #114: UserRole 에 viewer(조회 전용).
// 파일 위치: frontend/src/types/admin.ts
// 2026-09-23 수정 - 이슈 #73 P4: Step 1 목록 오퍼레이션 라벨 Approved drug · `step1_rld_collect` 추가.

export interface ApiIntegration {
  id: string
  code: string
  name: string
  provider: string
  grp: string | null
  endpoint: string
  method: string
  auth_type: string
  request_format: string
  is_active: boolean
  created_at: string
}

export interface CallStatsRow {
  bucket: string
  api_id: string
  api_code: string
  provider: string
  count: number
  success_count: number
  error_count: number
  avg_duration_ms: number | null
}

export interface AdminUserRef {
  id: string
  name: string
  email: string
  role: string
}

// 사용자 관리 화면용 (REQ-FN-ADMIN-06). 드롭다운 참조(AdminUserRef)의 상위 집합.
export interface AdminUserManage extends AdminUserRef {
  is_active: boolean
  created_at: string
  project_count: number
}

export type UserRole = 'researcher' | 'admin' | 'viewer'

export interface UserCreatePayload {
  email: string
  name: string
  password: string
  role: UserRole
}

export interface UserUpdatePayload {
  name?: string
  role?: UserRole
  is_active?: boolean
  password?: string
}

export interface AdminProjectRef {
  id: string
  name: string
}

export interface CallLog {
  id: string
  seq: number
  request_group_id: string | null
  api_id: string
  api_code: string
  api_name: string
  provider: string
  project_id: string | null
  project_name: string | null
  user_id: string | null
  user_name: string | null
  user_email: string | null
  step: number | null
  substep: string | null
  operation: string | null
  called_at: string
  success: boolean
  response_status: number | null
  duration_ms: number | null
  truncated: boolean
  request_bytes: number | null
  response_bytes: number | null
}

export interface CallLogDetail extends CallLog {
  tenant_id: string
  request_kind: string | null
  request: Record<string, unknown> | null
  response: Record<string, unknown> | null
  error_message: string | null
}

export interface CallLogPage {
  items: CallLog[]
  next_cursor: number | null
}

// 비즈니스 오퍼레이션 이름(타입) → 표시 라벨. group_id는 실행 1회 임시 상관 ID, operation은 작업 종류.
export const OP_LABELS: Record<string, string> = {
  project_new: 'Project · New',
  project_load: 'Project · Load',
  save: 'Save & Continue',
  report_generate: 'Report · Generate',
  step1_search: 'Step 1 · Search',
  step1_rld_detail: 'Step 1 · Approved drug detail',
  step1_rld_compare: 'Step 1 · Approved drug compare',
  step1_rld_collect: 'Step 1 · Approved drug collect',
  step1_snapshot_restore: 'Step 1 · Snapshot restore',
  step2_predict: 'Step 2 · Predict',
  step2_qtpp_save: 'Step 2 · QTPP save',
  step3_generate_runs: 'Step 3 · Generate runs',
  step3_upload_results: 'Step 3 · Upload results',
  step4_optimize: 'Step 4 · Optimize',
  step4_upload: 'Step 4 · Upload results',
  step4_complete: 'Step 4 · Complete',
  step5_generate_cases: 'Step 5 · Generate cases',
  step5_upload_y: 'Step 5 · Upload Y',
  step5_analyze: 'Step 5 · Analyze',
  step6_simulate: 'Step 6 · Simulate',
  step6_ai_summary: 'Step 6 · AI summary',
  step6_upload_observed: 'Step 6 · Upload observed',
  step6_pk_search: 'Step 6 · Literature search',  // 2026-09-24 #63
}

export function opLabel(op: string | null): string {
  if (!op) return 'operation'
  return OP_LABELS[op] ?? op
}

export interface OperationCatalogItem {
  code: string
  step: number | null
  name: string
  button: string | null
  triggers_external_api: boolean
  substeps: string[] | null
  source: string
  is_active: boolean
}

export interface AdminOperationLog {
  id: string
  seq: number
  operation: string
  operation_name: string
  step: number | null
  source: string
  success: boolean | null
  started_at: string
  user_id: string | null
  user_name: string | null
  user_email: string | null
  project_id: string | null
  project_name: string | null
  api_calls: number
}

export interface OperationLogPage {
  items: AdminOperationLog[]
  next_cursor: number | null
}

export interface OperationListFilters {
  operation?: string
  project_id?: string
  user_id?: string
  cursor?: number
  limit?: number
}

export interface UsageOperation {
  operation: string
  name: string
  step: number | null
  triggers_external_api: boolean
  count: number
}

export interface UsageProject {
  project_id: string | null
  project_name: string | null
  total_operations: number
  load_count: number
}

export interface CallListFilters {
  api_id?: string
  project_id?: string
  user_id?: string
  request_group_id?: string
  success?: boolean
  from?: string
  to?: string
  cursor?: number
  limit?: number
}
